import React from "react";
import { useQuery } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { ExternalLink, Loader2 } from "lucide-react";

import Badge from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ApplicantsKeys, getApplicant } from "../api/applicants";
import {
  getApplicantStatusColor,
  getApplicantStatusLabel,
  getMeetingVariantColor,
  getMeetingVariantLabel,
} from "../types/applicants";
import { getCountryName } from "../utils/countries";

interface ApplicantDetailsDialogProps {
  applicantId: string | null;
  onClose: () => void;
}

const ApplicantDetailsDialog: React.FC<ApplicantDetailsDialogProps> = ({
  applicantId,
  onClose,
}) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [ApplicantsKeys.GET_APPLICANT, applicantId],
    queryFn: () => getApplicant(applicantId!),
    enabled: !!applicantId,
  });

  const renderValue = (
    value: any,
    type: "boolean" | "string" | "date" = "string",
  ) => {
    if (type === "boolean") {
      return (
        <Badge
          color={value ? "purple" : "lightGrey"}
          variant={value ? "full" : "outline"}
        >
          {value ? "Yes" : "No"}
        </Badge>
      );
    }
    if (type === "date") {
      if (!value) {
        return <span className="text-muted-foreground">Not set</span>;
      }
      return new Date(value).toLocaleDateString();
    }
    return value || <span className="text-muted-foreground">Not set</span>;
  };

  return (
    <Dialog open={!!applicantId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Applicant Details</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-destructive">
            Failed to load applicant details
          </div>
        )}

        {data && (
          <ScrollArea className="h-[70vh] pr-2">
            <Tabs defaultValue="overview">
              <TabsList className="w-full">
                <TabsTrigger value="overview" className="flex-1">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="marketing" className="flex-1">
                  Marketing
                </TabsTrigger>
                <TabsTrigger value="tracking" className="flex-1">
                  Tracking & IDs
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Name
                    </label>
                    <p className="mt-1">
                      {renderValue(`${data.firstName} ${data.lastName}`)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Email
                    </label>
                    <p className="mt-1">{renderValue(data.email)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Country
                      </label>
                      <p className="mt-1">
                        {renderValue(getCountryName(data.country))}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Phone Number
                      </label>
                      <p className="mt-1">{renderValue(data.phoneNumber)}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      LinkedIn URL
                    </label>
                    {data.linkedinUrl ? (
                      <a
                        href={data.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 flex items-center gap-1 text-blue-600 hover:underline break-all"
                      >
                        {data.linkedinUrl}
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    ) : (
                      <p className="mt-1 text-muted-foreground">Not set</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Status
                      </label>
                      <div className="mt-1">
                        <Badge
                          color={getApplicantStatusColor(data.status)}
                          variant="full"
                        >
                          {getApplicantStatusLabel(data.status)}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Meeting Type
                      </label>
                      <div className="mt-1">
                        <Badge
                          color={getMeetingVariantColor(data.meetingVariant)}
                          variant="full"
                        >
                          {getMeetingVariantLabel(data.meetingVariant)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  {data.bookingUid && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Booking UID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.bookingUid)}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Cohort
                    </label>
                    <div className="mt-1">
                      {data.cohort && typeof data.cohort !== "string" ? (
                        <div>
                          <p className="text-base font-medium">
                            {formatInTimeZone(
                              data.cohort.startDate,
                              "America/New_York",
                              "MMM d, yyyy",
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            #{data.cohort.number}
                          </p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">Not assigned</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Created At
                      </label>
                      <p className="mt-1">
                        {renderValue(data.createdAt, "date")}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Updated At
                      </label>
                      <p className="mt-1">
                        {renderValue(data.updatedAt, "date")}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="marketing">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      UTM Source
                    </label>
                    <p className="mt-1">{renderValue(data.utmSource)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      UTM Campaign
                    </label>
                    <p className="mt-1">{renderValue(data.utmCampaign)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        UTM Content
                      </label>
                      <p className="mt-1">{renderValue(data.utmContent)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        UTM Term
                      </label>
                      <p className="mt-1">{renderValue(data.utmTerm)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Ad Set
                      </label>
                      <p className="mt-1">{renderValue(data.as)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Landing Page Version
                      </label>
                      <p className="mt-1">{renderValue(data.lpv)}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tracking">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Internal ID
                    </label>
                    <p className="mt-1 font-mono text-sm break-all">
                      {renderValue(data._id)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Stripe Customer ID
                    </label>
                    <p className="mt-1 font-mono text-sm break-all">
                      {renderValue(data.stripeCustomerId)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Attio Record ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.attioRecordId)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Attio Entry ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.attioEntryId)}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicantDetailsDialog;
